import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import i18n from "../../../i18n";
import { useNotifications } from "../useNotifications";

import * as zak from "../../api/zakooperApi";
import { nonEmpty } from "../../common/helpers/nonEmpty";
import * as gs from "../../api/groupSearchApi";
import * as plugins from "../../api/pluginsApi";

import pluginsMock from "../../assets/mocks/groupPricing/plugins.json";
import analyticsDataset from "../../assets/mocks/groupPricing/analytics-dataset.json";
import {
  buildGsSettings,
  isGsLiveGoal,
  mapGsStatusToSelectionState,
  modeToAnalyticsGoal,
} from "../../common/helpers/eplex/groupSearchMapping";
import { readGsDeliveryTerm } from "../../common/helpers/eplex/gsDeliveryTerm";
import { initializeSelectionDeliveryTerm } from "../../common/helpers/eplex/selectionDeliveryTermStorage";
import { DELIVERY_TERM_ANY } from "../../common/helpers/eplex/smartFiltersStorage";
import { defaultPivotPresetForGoal } from "../../common/helpers/eplex/pivotTemplates";
import { splitBrands, mergeBrands } from "../../common/helpers/eplex/brandsList";
import { formatDate } from "../../common/helpers/plugins/formatDateForBackend";
import { getUserEmail } from "../../common/helpers/getUserEmail";
import { generateUUID } from "../../common/helpers/generateUUID";
import { chunk } from "../../common/helpers/chunk";
import {
  groupSearchClonePayload,
  gsRowClonePayload,
  purchaseClonePayload,
  purchaseItemClonePayload,
} from "../../common/helpers/eplex/selectionRepeat";
import { isExpiredSearchResultsError } from "../../common/helpers/eplex/searchResultsExpired";
import { getSavedSelectionGoals, saveSelectionGoals } from "../../common/helpers/eplex/selectionGoalsStorage";
import { sortSelectionRunsByDateDesc } from "../../common/helpers/eplex/selectionRunOrder";
import {
  getSavedSelectionProgress,
  saveSelectionProgress,
  type SelectionProgressSnapshot,
} from "../../common/helpers/eplex/selectionProgressStorage";
import {
  getSavedSelectionNegotiations,
  saveSelectionNegotiations,
} from "../../common/helpers/eplex/selectionNegotiationStorage";
import {
  clearSelectionDraft,
  loadSelectionDraft,
  saveSelectionDraft,
} from "../../common/helpers/eplex/selectionDraftStorage";

// Color palette to assign deterministic colors to live plugins (no color in API)
const SUPPLIER_PALETTE = [
  "#1E40AF",
  "#2D6A4F",
  "#B45309",
  "#6D28D9",
  "#0E7490",
  "#9B2C3F",
  "#7C2D12",
  "#1B5E3A",
  "#581C87",
  "#0F766E",
];
function colorForPluginId(id: number): string {
  const n = Math.abs(id) % SUPPLIER_PALETTE.length;
  return SUPPLIER_PALETTE[n];
}

// Размер пакета при первичной загрузке позиций (закупка/КП и «Другое»): большие
// списки (до 10k) грузим частями, чтобы не упереться в размер тела запроса/таймаут.
const UPLOAD_BATCH_SIZE = 1000;

import {
  DEFAULT_STRATEGY_ALGOS,
  type SelectionGoal,
  type SelectionRun,
  type SelectionState,
  type SelectionStrategy,
  type SelectionProcessLogEntry,
  type SelectionWizardState,
  type SelectionNegotiationConfig,
} from "../../models/eplex/Selection";
import type {
  GpPivotPreset,
  GpPlugin,
  GpPluginGroup,
  GpPluginRank,
  GpPositionRow,
} from "../../models/eplex/GroupPricing";

const POLL_INTERVAL_MS = 4000;

/**
 * Goals, требующие живой Zakooper API (purchase flow).
 * Analytics-цели (competitors/monitoring/pricing) — отдельная подсистема
 * `group_search`, см. {@link isGsLiveGoal}.
 */
const LIVE_GOALS: SelectionGoal[] = ["procurement", "tender"];

export function isLiveGoal(goal: SelectionGoal): boolean {
  return LIVE_GOALS.includes(goal);
}

// Типы, видимые в левом списке подборов: Закупка, КП и Другое. Аналитические
// цели (конкуренты/мониторинг/ценообразование/прогноз) скрыты из списка; в
// мастере на шаге «задача» они остаются со статусом «скоро».
const LISTED_GOALS: SelectionGoal[] = ["procurement", "tender", "other"];

export function isListedGoal(goal: SelectionGoal): boolean {
  return LISTED_GOALS.includes(goal);
}

/** Любой goal, который ходит на реальный backend (zakooper или group_search). */
export function isAnyLiveGoal(goal: SelectionGoal): boolean {
  return isLiveGoal(goal) || isGsLiveGoal(goal);
}

/**
 * Достать осмысленную ошибку из axios-ответа. Backend Eplex заворачивает
 * детальный текст в `data.error.message` в виде «Неопознанная ошибка Data: {...,"err":"..."}`,
 * а наш `apiJwt`-интерсептор уже его парсит — но reject отдаёт AxiosError
 * с дженериком `Request failed with status code 500`. Этот helper заглядывает
 * внутрь `error.response.data` и достаёт читаемый текст.
 */
function getBackendInnerError(error: unknown): string | null {
  if (!error || typeof error !== "object") return null;
  const axiosLike = error as {
    response?: { data?: { error?: { message?: string } } };
  };
  const raw = axiosLike.response?.data?.error?.message;
  if (!raw) return null;
  const match = /Data:\s*(\{.*\})/.exec(raw);
  if (!match) return raw;
  try {
    const parsed = JSON.parse(match[1]) as { err?: string; message?: string; error_code?: number };
    return parsed.err ?? parsed.message ?? `error_code: ${parsed.error_code ?? "?"}`;
  } catch {
    return raw;
  }
}

function stateFromZak(z: zak.ZakState, hasNegotiation: boolean): SelectionState {
  if (z === 1) return "draft";
  if (z === 2) return "pricing";
  if (z === 3) return hasNegotiation ? "negotiation" : "planning";
  if (z === 4) return "negotiation";
  if (z === 5) return "purchasing";
  if (z === 6) return "completed";
  if (z === 7) return "cancelled";
  return "draft";
}

function emptyStrategy(): SelectionStrategy {
  return {
    preset: "balanced",
    algos: [...DEFAULT_STRATEGY_ALGOS],
    brandsTrusted: "",
    brandsTrustedOn: true,
    brandsExcludeOn: true,
    maxTime: 7,
    priceDiffPercent: 15,
    extraDays: 1, // запас по сроку по умолчанию — 1 день
    orderType: 1,
    isAutoorder: 0,
  };
}

// Крайний срок по умолчанию — сегодня + 3 дня (ISO yyyy-mm-dd), предзаполняем
// в мастере, чтобы пользователь не оставлял поле пустым.
function defaultDeadlineIso(): string {
  const d = new Date();
  d.setDate(d.getDate() + 3);
  return d.toISOString().slice(0, 10);
}

function emptyWizardState(): SelectionWizardState {
  return {
    goal: null,
    title: "",
    docOsn: { uuid: null, title: "" },
    deadline: defaultDeadlineIso(),
    positions: [{ article: "", brand: null, qty: 1 }],
    plugins: [],
    strategy: emptyStrategy(),
    negotiation: {
      enabled: false,
      clientEmail: "",
      clientPhone: "",
      managerName: "",
      managerEmail: "",
      managerComment: "",
      tmPercent: 15,
      ttlDays: 5,
      prepay: 0,
      isAutostart: 0,
      isAllowedOrders: 0,
    },
    analytics: {
      competitorPluginIds: [],
      pivotPreset: { rows: ["article", "brand"], cols: [], vals: [{ field: "cost", agg: "min" }], filters: [] },
      schedule: "once",
      notifyEmail: "",
      notifyOnFinish: true,
      triggers: [],
    },
  };
}

function mapZakPurchase(
  z: zak.ZakPurchaseDto,
  goal: SelectionGoal = "procurement",
  prevTotal = 0,
  /**
   * Ранее показанный прогресс (finished). Прогресс активной закупки монотонен:
   * не даём ему откатиться к устаревшему `gs_data`, когда карточка не открыта и
   * live-инфо из GS не тянется (иначе при переключении между закупками полоса
   * «сбрасывается» на 0).
   */
  prevFinished = 0,
  /**
   * Опциональный live-снимок прогресса из /eplex/group_search/{gs_request_id}.
   * Zakooper-эндпоинт `getPurchase` обновляет `gs_data.info.finished` рывками
   * (по нашим наблюдениям — только в момент перехода между state), что даёт
   * UX «0 → 28/28 спустя минуту». Если параллельно дёргать сам GS — там info
   * реальная per-request, прогресс растёт плавно. Передаём этот override
   * сюда из reloadOne, чтобы пользователь видел нормальную полосу.
   */
  gsLiveInfo?: { total: number; finished: number } | null,
  /**
   * Сохранённый снимок настроек согласования из wizard'а. Бэк zakooper этот
   * объект не возвращает (там своя модель `negotiation` под backend-овский
   * флоу), поэтому при reload мы должны самостоятельно протащить wizard-данные
   * через перезагрузки списка — иначе при первом же refresh они потеряются.
   */
  prevNegotiation?: SelectionRun["negotiation"],
): SelectionRun {
  // Если есть live info из GS — она приоритетнее, чем stale gs_data в purchase.
  const stateNum = z.state;
  const gsTotal = gsLiveInfo?.total ?? z.gs_data?.info?.total ?? 0;
  const gsFinished = gsLiveInfo?.finished ?? z.gs_data?.info?.finished ?? 0;
  // After state >= 3 the API stops returning gs_data — keep previous total
  // (so the detail screen still shows "N из N" rather than "0 из 0").
  const positionsTotal = gsTotal || prevTotal;
  let positionsFinished = gsFinished;
  if (stateNum >= 3 && positionsTotal > 0) {
    positionsFinished = positionsTotal;
  } else if (stateNum <= 2) {
    // Активная фаза: прогресс не откатываем к устаревшему значению.
    positionsFinished = Math.max(gsFinished, prevFinished);
  }
  return {
    uuid: z.uuid,
    purchaseUuid: z.uuid,
    // request_id группового поиска закупки — источник живых результатов проценки
    // (рекомендации готовятся позже, а офферы доступны через контент строк GS).
    gsRequestId: z.gs_request_id ?? undefined,
    goal,
    title: z.title,
    state: stateFromZak(z.state, z.negotiation_state !== null),
    docOsn: z.doc_osn_uuid ? { uuid: z.doc_osn_uuid, title: z.doc_osn_title ?? "" } : null,
    createdAt: z.created_at,
    updatedAt: z.updated_at,
    deadlineAt: z.purchase_deadline,
    positionsTotal,
    positionsFinished,
    totalAmount: null,
    averageTime: null,
    pluginsCount: z.plugins?.length ?? 0,
    strategy: {
      preset: "custom",
      algos: z.algos ?? [1, 2, 3, 4, 5, 6, 7, 8],
      brandsTrusted: z.brands_trusted ?? "",
      brandsTrustedOn: true,
      brandsExcludeOn: true,
      maxTime: 7,
      priceDiffPercent: z.price_diff_percent ?? 0,
      extraDays: z.extra_days ?? 0,
      orderType: z.order_type === 2 ? 2 : 1,
      isAutoorder: z.is_autoorder,
    },
    negotiation: prevNegotiation,
  };
}

interface MockRun extends SelectionRun {
  mockProgress: { tickStartedAt: number; durationMs: number };
}

/**
 * Маппинг GS DTO → SelectionRun. Goal у GS-проценок хранится в localStorage
 * (как и у zakooper purchases) — серверного goal-поля нет.
 */
function mapGsItemToRun(item: gs.GsItemDto, goal: SelectionGoal, prevTotal = 0): SelectionRun {
  const total = item.info?.total ?? prevTotal;
  const finished = item.info?.finished ?? 0;
  // Состояние считаем по статусу ВМЕСТЕ с очередью: сам по себе статус в
  // середине проценки успевает побывать «завершённым» (см. CAB-738).
  const state = mapGsStatusToSelectionState(item.status, item.info);
  const createdIso = new Date(item.date * 1000).toISOString();
  return {
    uuid: `gs-${item.request_id}`,
    purchaseUuid: null,
    gsRequestId: item.request_id,
    gsClientId: item.client_id,
    goal,
    title: item.settings.name,
    state,
    docOsn: null,
    createdAt: createdIso,
    updatedAt: createdIso,
    deadlineAt: null,
    positionsTotal: total,
    positionsFinished: finished,
    totalAmount: null,
    averageTime: null,
    pluginsCount: item.settings.plugins?.length ?? 0,
    strategy: {
      preset: "custom",
      algos: [],
      brandsTrusted: "",
      brandsTrustedOn: true,
      brandsExcludeOn: true,
      // Старый контракт нормализуется на входе; внутри ЛК шкала единая (1–40 / 41).
      maxTime: readGsDeliveryTerm(item.settings) ?? DELIVERY_TERM_ANY,
      priceDiffPercent: item.settings.analyze?.cost_excess_percent ?? 0,
      extraDays: 0,
      orderType: 1,
      isAutoorder: 0,
    },
    analytics: {
      competitorPluginIds: item.settings.plugins ?? [],
      pivotPreset: { rows: ["article", "brand"], cols: [], vals: [{ field: "cost", agg: "min" }], filters: [] },
      schedule: scheduleFromClientPeriod(item.settings.pricekeeper?.client_period),
      notifyEmail: item.settings.notify?.email ?? "",
      notifyOnFinish: item.settings.notify?.on_finish ?? true,
      triggers: (item.settings.pricekeeper?.triggers ?? []).map((trigger, idx) => ({
        id: `t-${item.request_id}-${idx}`,
        type: trigger.type,
        param: trigger.params?.[0]?.value,
      })),
    },
  };
}

/** Период обновления Pricekeeper (в днях) → расписание подбора. */
function scheduleFromClientPeriod(period: number | undefined): "daily" | "weekly" | "monthly" | "once" {
  if (period === 1) return "daily";
  if (period === 7) return "weekly";
  return period === 30 ? "monthly" : "once";
}

export const useSelectionStore = defineStore("eplexSelection", () => {
  const runs = ref<SelectionRun[]>([]);
  const algoDescriptions = ref<Record<string, string>>({});
  const isLoadingList = ref(false);
  const listLoadError = ref<string | null>(null);
  // Идёт запуск подбора из wizard'а — блокирует кнопку «Запустить» и, главное,
  // гарантирует идемпотентность запуска (см. startRunFromWizard).
  const isStarting = ref(false);
  // Идёт повтор подбора копией (CAB-677) — кнопка «Повторить подбор» блокируется.
  const isRepeating = ref(false);
  // Список процессов грузим один раз за сессию. Без этого флага триггер из
  // SideBar (ensureProcessesLoaded) перезапускал loadList на каждой навигации
  // по eplex для аккаунтов без процессов (runs всегда пуст), дублируя
  // group_search и installed. reset() (смена аккаунта) сбрасывает флаг.
  const listLoaded = ref(false);

  // Local map: which goal user picked when creating this purchase.
  // Backend doesn't distinguish "procurement" vs "tender" — both are zakooper purchases.
  const goalByUuid = ref<Record<string, SelectionGoal>>(getSavedSelectionGoals());
  function persistGoalMap() {
    saveSelectionGoals(goalByUuid.value);
  }
  function setGoalForUuid(uuid: string, goal: SelectionGoal) {
    goalByUuid.value[uuid] = goal;
    persistGoalMap();
  }

  // Снимок согласования КП по uuid закупки. Backend не хранит его до /negotiate, а
  // в памяти он теряется на перезагрузке — сохраняем в localStorage, чтобы форма
  // отправки предзаполнялась значениями из wizard'а (наценка, срок ссылки, контакты).
  const negotiationByUuid = ref<Record<string, SelectionNegotiationConfig>>(getSavedSelectionNegotiations());
  function setNegotiationForUuid(uuid: string, config: SelectionNegotiationConfig) {
    negotiationByUuid.value[uuid] = config;
    saveSelectionNegotiations(negotiationByUuid.value);
  }

  // CAB-627: backend не всегда переводит purchase.state в completed сразу после
  // того, как реально все позиции оформлены (заказано/ошибка — ни одной
  // in-flight). Вкладка «Заказы поставщикам» видит это первой и сообщает сюда,
  // чтобы погасить точку-индикатор в сайдбаре, не дожидаясь backend-статуса.
  // Живёт только в памяти сессии (не персистится) — переживает поллинг, так
  // как tickPurchases/loadList/reloadOne целиком заменяют объект run.
  const purchaseOrdersSettledByUuid = ref<Record<string, boolean>>({});
  function markPurchaseOrdersSettled(uuid: string, settled: boolean) {
    purchaseOrdersSettledByUuid.value[uuid] = settled;
  }

  // Последний показанный прогресс «finished из total» по uuid — переживает F5:
  // свежий listPurchases приходит со stale gs_data (0/0), и до первого live-опроса
  // GS полоса прогресса «сбрасывалась». Снимок пишем при каждом изменении runs.
  const progressByUuid = ref<Record<string, SelectionProgressSnapshot>>(getSavedSelectionProgress());
  watch(
    () => runs.value.map((r) => `${r.uuid}:${r.positionsTotal}:${r.positionsFinished}`).join("|"),
    () => {
      const next: Record<string, SelectionProgressSnapshot> = {};
      for (const run of runs.value) {
        if (run.positionsTotal > 0) {
          next[run.uuid] = { total: run.positionsTotal, finished: run.positionsFinished };
        }
      }
      progressByUuid.value = next;
      saveSelectionProgress(next);
    },
  );

  const warehouses = ref<GpPlugin[]>([]);
  const suppliers = ref<GpPlugin[]>([]);
  const pluginGroups = ref<GpPluginGroup[]>([]);
  const trustedBrandsCatalog = ref<string[]>(pluginsMock.trustedBrandsCatalog);
  const isLoadingPlugins = ref(false);
  const pluginsLoadError = ref<string | null>(null);
  const analyticsDatasetRef = ref(analyticsDataset.dataset);
  const fieldDefinitions = ref(analyticsDataset.fieldDefinitions);

  /**
   * Загрузка «в полёте»: параллельные вызовы ждут ЕЁ, а не уходят ни с чем. Раньше
   * второй вызов возвращался сразу, и код, которому каталог нужен по делу (сверка
   * набора поставщиков перед запуском), продолжал с пустыми списками (CAB-696).
   */
  let pluginsInflight: Promise<void> | null = null;

  async function loadPlugins() {
    if (pluginsInflight) return pluginsInflight;
    if (warehouses.value.length > 0 || suppliers.value.length > 0) return;

    pluginsInflight = loadPluginsOnce().finally(() => {
      pluginsInflight = null;
    });

    return pluginsInflight;
  }

  async function loadPluginsOnce() {
    isLoadingPlugins.value = true;
    pluginsLoadError.value = null;
    try {
      const [installed, whList, groups] = await Promise.all([
        plugins.listInstalledPlugins(),
        plugins.listWarehousePlugins(),
        plugins.listPluginGroups().catch(() => [] as plugins.PluginGroupDto[]),
      ]);

      const whIds = new Set(whList.map((w) => Number(w.id)));
      const whMapped: GpPlugin[] = whList.map((w) => ({
        id: Number(w.id),
        title: w.title,
        plugin_name: w.plugin_name,
        group: w.group,
        icon: w.icon,
        wh_uuid: w.wh_uuid,
        color: colorForPluginId(Number(w.id)),
      }));
      const supMapped: GpPlugin[] = installed
        .filter((p) => !whIds.has(Number(p.id)))
        .map((p) => ({
          id: Number(p.id),
          title: p.title,
          plugin_name: p.plugin_name,
          group: p.group,
          icon: p.icon,
          color: colorForPluginId(Number(p.id)),
        }));

      warehouses.value = whMapped;
      suppliers.value = supMapped;
      pluginGroups.value = groups.map((g) => {
        const colorObj =
          typeof g.color === "object" && g.color !== null ? `rgb(${g.color.R}, ${g.color.G}, ${g.color.B})` : g.color;
        return {
          id: g.id,
          name: g.name,
          count: g.count,
          color: colorObj,
          is_public: g.is_public === 1 ? 1 : 0,
        };
      });

      // Каталог приехал — самое время выкинуть из выбора чужие id (CAB-696)
      pruneUnknownPlugins();
    } catch (error) {
      pluginsLoadError.value = error instanceof Error ? error.message : "Не удалось загрузить поставщиков";
      // Без mock-фолбэка: при ошибке загрузки оставляем списки пустыми и
      // показываем ошибку. Подставлять чужие/искажённые данные нельзя — они
      // всё равно нерабочие и только путают пользователя.
      warehouses.value = [];
      suppliers.value = [];
      pluginGroups.value = [];
    } finally {
      isLoadingPlugins.value = false;
    }
  }

  /**
   * Сброс каталога, чтобы `loadPlugins` перечитал его заново (CAB-696).
   *
   * `loadPlugins` намеренно идемпотентен — второй заход при непустых списках не
   * делает ничего, — поэтому смена каталога под открытым кабинетом (выход из
   * демо-доступа) до «Подбора» иначе не доезжает вовсе: до перезагрузки страницы
   * мастер продолжал показывать демо-поставщиков.
   */
  function resetPluginCatalog(): void {
    warehouses.value = [];
    suppliers.value = [];
    pluginGroups.value = [];
    pluginsLoadError.value = null;
  }

  const mode = ref<"list" | "wizard" | "detail">("list");
  const wizardState = ref<SelectionWizardState>(emptyWizardState());
  const wizardStep = ref(0);
  const wizardMaxReached = ref(0);
  const selectedRunUuid = ref<string | null>(null);

  const searchQuery = ref("");
  const goalFilter = ref<SelectionGoal | "all">("all");

  // Список подборов всегда «новые сверху»: `runs` — это склейка zakooper-закупок
  // (отданных бэком по updated_at) и group_search-проценок, поэтому собственного
  // порядка у него нет. Сортируем один раз здесь — группы ниже наследуют порядок.
  const filteredRuns = computed(() => {
    const q = searchQuery.value.trim().toLowerCase();
    const visible = runs.value
      .filter((run) => {
        if (run.state === "archived") return false;
        if (!isListedGoal(run.goal)) return false;
        if (goalFilter.value !== "all" && run.goal !== goalFilter.value) return false;
        if (!q) return true;
        return run.title.toLowerCase().includes(q) || (run.docOsn?.title ?? "").toLowerCase().includes(q);
      })
      // CAB-627: оверрайд живёт отдельно от run (см. purchaseOrdersSettledByUuid),
      // подмешиваем его здесь, чтобы переживать замену объекта run поллингом.
      .map((run) =>
        run.purchaseUuid && purchaseOrdersSettledByUuid.value[run.purchaseUuid] ? { ...run, ordersSettled: true } : run,
      );
    return sortSelectionRunsByDateDesc(visible);
  });

  // Группы наследуют порядок filteredRuns («новые сверху») — раскладываем по
  // группам, сохраняя относительный порядок элементов.
  const groupedRuns = computed(() => {
    const groups: Record<string, SelectionRun[]> = {
      active: [],
      drafts: [],
      completed: [],
    };
    for (const run of filteredRuns.value) {
      if (run.state === "draft") groups.drafts.push(run);
      else if (run.state === "completed" || run.state === "cancelled") groups.completed.push(run);
      else groups.active.push(run);
    }
    return groups;
  });

  const selectedRun = computed<SelectionRun | null>(() => {
    if (!selectedRunUuid.value) return null;
    return runs.value.find((r) => r.uuid === selectedRunUuid.value) ?? null;
  });

  const activeProcesses = computed<SelectionProcessLogEntry[]>(() => {
    return runs.value
      .filter((r) => r.state === "pricing" || r.state === "purchasing")
      .map((r) => ({
        id: r.uuid,
        runId: r.uuid,
        title: r.title,
        goal: r.goal,
        state: r.state,
        progress: r.positionsTotal > 0 ? r.positionsFinished / r.positionsTotal : 0,
        positionsFinished: r.positionsFinished,
        positionsTotal: r.positionsTotal,
        etaSeconds: Math.max(0, (r.positionsTotal - r.positionsFinished) * 2),
        startedAt: r.updatedAt,
      }));
  });

  // Пять последних завершённых — именно последних: без сортировки в срез
  // попадали произвольные пять (порядок runs задаёт бэк, а не дата).
  const completedProcesses = computed<SelectionProcessLogEntry[]>(() => {
    return sortSelectionRunsByDateDesc(runs.value.filter((r) => r.state === "completed"))
      .slice(0, 5)
      .map((r) => ({
        id: r.uuid,
        runId: r.uuid,
        title: r.title,
        goal: r.goal,
        state: r.state,
        progress: 1,
        positionsFinished: r.positionsTotal,
        positionsTotal: r.positionsTotal,
        etaSeconds: 0,
        startedAt: r.updatedAt,
      }));
  });

  async function loadList() {
    isLoadingList.value = true;
    listLoadError.value = null;
    // перечитываем цели/снимки согласования текущего аккаунта (на случай смены пользователя без перезагрузки)
    goalByUuid.value = getSavedSelectionGoals();
    negotiationByUuid.value = getSavedSelectionNegotiations();
    // Load plugins in parallel (idempotent)
    void loadPlugins();
    try {
      const [purchasesList, algos, gsList] = await Promise.all([
        zak.listPurchases({ limit: 50, order_by: "updated_at", order_dir: "DESC", is_archive: 0 }),
        algoDescriptions.value && Object.keys(algoDescriptions.value).length > 0
          ? Promise.resolve(algoDescriptions.value)
          : zak.getAlgoDescriptions(),
        // Без `gs_type` — backend возвращает все наши GS, включая завершённые.
        // `gs_type=2` фильтрует только активные/новые → теряем завершённую аналитику.
        gs.listGroupSearches().catch(() => ({ items: [], count: 0 })),
      ]);
      algoDescriptions.value = algos;

      const liveRuns = purchasesList.data.map((p) => {
        const goal = goalByUuid.value[p.uuid] ?? "procurement";
        const prev = runs.value.find((r) => r.uuid === p.uuid);
        // На свежей загрузке runs пуст → prev нет; восстанавливаем снимок согласования
        // из localStorage, а прогресс — из последнего сохранённого снимка (иначе
        // полоса «сбрасывается» в 0 из 0 до первого live-опроса GS).
        const savedProgress = progressByUuid.value[p.uuid];
        return mapZakPurchase(
          p,
          goal,
          prev?.positionsTotal ?? savedProgress?.total ?? 0,
          prev?.positionsFinished ?? savedProgress?.finished ?? 0,
          null,
          prev?.negotiation ?? negotiationByUuid.value[p.uuid],
        );
      });
      // /group_search возвращает также GS, авто-созданные zakooper-бэкендом
      // под покупки (procurement / tender) — у них name начинается с
      // "Zakooper (". Их пропускаем: они уже в `liveRuns` из listPurchases.
      const gsRuns = gsList.items
        .filter((item) => !item.settings.name?.startsWith("Zakooper ("))
        .map((item) => {
          const uuid = `gs-${item.request_id}`;
          // Если в localStorage есть запись — используем её. Иначе восстанавливаем
          // goal из серверного settings.mode (так не падаем на дефолт competitors
          // для monitoring/pricing после перезагрузки страницы).
          const goal = goalByUuid.value[uuid] ?? modeToAnalyticsGoal(item.settings.mode);
          const prev = runs.value.find((r) => r.uuid === uuid);
          return mapGsItemToRun(item, goal, prev?.positionsTotal ?? progressByUuid.value[uuid]?.total ?? 0);
        });
      // Сохраняем только forecast-mock runs (forecast — без бэка)
      const forecastMocks = runs.value.filter((r) => r.goal === "forecast");
      runs.value = [...liveRuns, ...gsRuns, ...forecastMocks];
      // Помечаем загруженным только при успехе — при ошибке оставляем false,
      // чтобы следующая навигация повторила попытку (auto-recovery).
      listLoaded.value = true;
    } catch (error) {
      listLoadError.value = error instanceof Error ? error.message : "Не удалось загрузить список";
      runs.value = runs.value.filter((r) => r.goal === "forecast");
    } finally {
      isLoadingList.value = false;
    }
  }

  /**
   * Перезагрузить один GS-run по `request_id`. Используется поллингом.
   * Сначала пробуем `getGroupSearch(uuid)` (full settings), при ошибке —
   * fallback на flows (легче по нагрузке, но без полных settings).
   */
  async function reloadGsOne(gsRequestId: string): Promise<SelectionRun | null> {
    try {
      const { item } = await gs.getGroupSearch(gsRequestId);
      const uuid = `gs-${item.request_id}`;
      const idx = runs.value.findIndex((r) => r.uuid === uuid);
      const prevTotal = idx >= 0 ? runs.value[idx].positionsTotal : 0;
      const goal = goalByUuid.value[uuid] ?? runs.value[idx]?.goal ?? modeToAnalyticsGoal(item.settings.mode);
      const mapped = mapGsItemToRun(item, goal, prevTotal);
      if (idx >= 0) runs.value[idx] = mapped;
      else runs.value.unshift(mapped);
      settleGsStopFlag(item.request_id, mapped.state);
      return mapped;
    } catch {
      return null;
    }
  }

  async function reloadOne(uuid: string, options?: { silent?: boolean }) {
    try {
      const z = await zak.getPurchase(uuid, { silent: options?.silent });
      const idx = runs.value.findIndex((r) => r.uuid === uuid);
      const prevTotal = idx >= 0 ? runs.value[idx].positionsTotal : 0;
      const prevFinished = idx >= 0 ? runs.value[idx].positionsFinished : 0;
      const goal = goalByUuid.value[uuid] ?? runs.value[idx]?.goal ?? "procurement";

      // Подтянем live-прогресс напрямую из group_search, если он есть и
      // покупка ещё в активной фазе (state 1-2). Zakooper-эндпоинт обновляет
      // `gs_data.info.finished` рывками, а GS — реалтайм. Только во время
      // активной фазы; после state 3+ purchase сам себя «закрывает».
      let gsLiveInfo: { total: number; finished: number } | null = null;
      if (z.gs_request_id && z.state <= 2) {
        try {
          const { item } = await gs.getGroupSearch(z.gs_request_id);
          if (item.info) {
            gsLiveInfo = {
              total: item.info.total ?? 0,
              finished: item.info.finished ?? 0,
            };
          }
        } catch {
          // GS-эндпоинт упал — это не критично, останемся на stale gs_data
        }
      }

      const prevNegotiation = (idx >= 0 ? runs.value[idx].negotiation : undefined) ?? negotiationByUuid.value[uuid];
      const mapped = mapZakPurchase(z, goal, prevTotal, prevFinished, gsLiveInfo, prevNegotiation);
      if (idx >= 0) runs.value[idx] = mapped;
      else runs.value.unshift(mapped);
      return mapped;
    } catch {
      return null;
    }
  }

  // ── Wizard ──────────────────────────────────────────────────────────────

  // Набор поставщиков запоминаем ОТДЕЛЬНО по каждой цели и по логину: у «Закупки»
  // свой сохранённый набор, у «Другого» свой, и они не пересекаются между аккаунтами.
  const SAVED_PLUGINS_BASE = "selection.wizard.plugins";

  function savedPluginsKey(goal: SelectionGoal | null): string {
    const email = getUserEmail();
    const scope = goal ?? "default";
    return email ? `${SAVED_PLUGINS_BASE}.${email}.${scope}` : `${SAVED_PLUGINS_BASE}.${scope}`;
  }

  function loadSavedPlugins(goal: SelectionGoal | null): GpPluginRank[] {
    try {
      const raw = localStorage.getItem(savedPluginsKey(goal));
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed
          .filter((x) => x && typeof x === "object" && typeof x.id === "number" && typeof x.rank === "number")
          .map((x) => ({ id: x.id, rank: x.rank }));
      }
    } catch (error) {
      console.error("selection: cannot load saved plugins", error);
    }
    return [];
  }

  function persistSelectedPlugins() {
    try {
      localStorage.setItem(savedPluginsKey(wizardState.value.goal), JSON.stringify(wizardState.value.plugins));
    } catch (error) {
      console.error("selection: cannot persist plugins", error);
    }
  }

  /** id площадок, которые реально есть у пользователя. Пустой набор = каталог ещё не загружен. */
  function knownPluginIds(): Set<number> {
    const ids = new Set<number>();
    for (const plugin of warehouses.value) ids.add(Number(plugin.id));
    for (const plugin of suppliers.value) ids.add(Number(plugin.id));

    return ids;
  }

  /**
   * Сохранённый набор поставщиков сверяем с каталогом и выкидываем чужие id (CAB-696).
   *
   * Набор живёт в localStorage по логину и переживает любые изменения каталога. После
   * выхода из демо-доступа демо-поставщики из аккаунта исчезают, а их id остаются в
   * наборе — и дальше расходятся две картины: счётчик мастера показывал «7
   * поставщиков», в окне выбора был один свой (окно и так прячет неизвестные id), а на
   * бэк уходили все семь чужих, и закупка возвращалась без результатов вовсе.
   *
   * Чистим состояние, а не только показ: и счётчик, и запрос читают wizardState.plugins,
   * иначе одно из двух мест снова разъедется. Пустой каталог (не загрузился, отказ сети)
   * не повод чистить — иначе сбой загрузки стирал бы выбор человека.
   */
  function pruneUnknownPlugins(): void {
    const known = knownPluginIds();
    if (known.size === 0) return;

    const kept = wizardState.value.plugins.filter((p) => known.has(Number(p.id)));
    if (kept.length === wizardState.value.plugins.length) return;

    wizardState.value.plugins = kept;
    // Хранилище тоже: иначе следующий мастер поднимет те же чужие id
    persistSelectedPlugins();
  }

  /**
   * Набор поставщиков для отправки на бэк — только свои, гарантированно (CAB-696).
   *
   * Сверка при открытии мастера может не состояться: каталог в тот момент ещё грузился,
   * мастер восстановили из черновика, площадку отключили уже после выбора. Поэтому
   * последнее слово — здесь, на выходе: каталог дожидаемся (`loadPlugins` идемпотентен,
   * при загруженном отвечает сразу) и чистим набор ровно перед запросом. Иначе чужой id
   * уезжает на бэк, и проценка возвращается пустой по ВСЕМ поставщикам, включая своих.
   */
  async function pluginsForLaunch(): Promise<GpPluginRank[]> {
    await loadPlugins();
    pruneUnknownPlugins();

    return wizardState.value.plugins;
  }

  // Доверенные / исключаемые бренды и их свитчеры — предпочтение уровня пользователя
  // (не зависит от цели): запоминаем по логину и подставляем в каждый новый подбор.
  const SAVED_BRANDS_BASE = "selection.wizard.brands";

  interface SavedBrandPrefs {
    brandsTrusted: string;
    brandsTrustedOn: boolean;
    brandsExcludeOn: boolean;
  }

  function savedBrandsKey(): string {
    const email = getUserEmail();
    return email ? `${SAVED_BRANDS_BASE}.${email}` : SAVED_BRANDS_BASE;
  }

  function loadSavedBrandPrefs(): SavedBrandPrefs | null {
    try {
      const raw = localStorage.getItem(savedBrandsKey());
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<SavedBrandPrefs>;
      if (typeof parsed?.brandsTrusted !== "string") return null;
      return {
        brandsTrusted: parsed.brandsTrusted,
        brandsTrustedOn: parsed.brandsTrustedOn ?? true,
        brandsExcludeOn: parsed.brandsExcludeOn ?? true,
      };
    } catch (error) {
      console.error("selection: cannot load saved brand prefs", error);
      return null;
    }
  }

  function persistBrandPrefs() {
    try {
      const s = wizardState.value.strategy;
      const prefs: SavedBrandPrefs = {
        brandsTrusted: s.brandsTrusted,
        brandsTrustedOn: s.brandsTrustedOn,
        brandsExcludeOn: s.brandsExcludeOn,
      };
      localStorage.setItem(savedBrandsKey(), JSON.stringify(prefs));
    } catch (error) {
      console.error("selection: cannot persist brand prefs", error);
    }
  }

  function applySavedBrandPrefs() {
    const prefs = loadSavedBrandPrefs();
    if (!prefs) return;
    wizardState.value.strategy.brandsTrusted = prefs.brandsTrusted;
    wizardState.value.strategy.brandsTrustedOn = prefs.brandsTrustedOn;
    wizardState.value.strategy.brandsExcludeOn = prefs.brandsExcludeOn;
  }

  // Пока пользователь правит бренды/свитчеры в мастере — запоминаем выбор на будущее.
  watch(
    () => [
      wizardState.value.strategy.brandsTrusted,
      wizardState.value.strategy.brandsTrustedOn,
      wizardState.value.strategy.brandsExcludeOn,
    ],
    () => {
      if (mode.value === "wizard") persistBrandPrefs();
    },
  );

  function openWizard() {
    wizardState.value = emptyWizardState();
    // Набор поставщиков подставим при выборе цели (у каждой цели свой сохранённый
    // набор — см. pickGoal); на старте цель ещё не выбрана.
    wizardState.value.plugins = loadSavedPlugins(wizardState.value.goal);
    pruneUnknownPlugins();
    // Доверенные/исключаемые бренды и свитчеры — общие для пользователя, подставляем.
    applySavedBrandPrefs();
    wizardStep.value = 0;
    wizardMaxReached.value = 0;
    selectedRunUuid.value = null;
    mode.value = "wizard";
    void loadPlugins();
  }

  /**
   * Мастер нового подбора по прежнему (CAB-677): результаты удалены по сроку хранения,
   * а перезапустить ту же закупку бэк не даёт («старт возможен только в статусе
   * Черновик»). Переносим цель, название, стратегию, снимки согласования/аналитики и
   * позиции; поставщики — сохранённый набор цели, как у любого нового подбора.
   * Пустой список позиций (удалены вместе с групповым поиском) оставляет редактор
   * позиций пустым. Открываем сразу шаг «Позиции»: цель уже известна.
   */
  function openWizardFromRun(run: SelectionRun, positions: GpPositionRow[]) {
    openWizard();
    pickGoal(run.goal);
    const state = wizardState.value;
    state.title = run.title;
    state.strategy = { ...run.strategy, algos: [...run.strategy.algos] };
    if (run.negotiation) state.negotiation = { ...run.negotiation };
    if (run.analytics) {
      state.analytics = {
        ...run.analytics,
        competitorPluginIds: [...run.analytics.competitorPluginIds],
        triggers: run.analytics.triggers.map((trigger) => ({ ...trigger })),
      };
    }
    if (positions.length > 0) {
      state.positions = [...positions, { article: "", brand: null, qty: 1 }];
    }
    setStep(1);
  }

  function pickGoal(goal: SelectionGoal) {
    wizardState.value.goal = goal;
    // Подставляем сохранённый набор поставщиков именно этой цели (по логину).
    wizardState.value.plugins = loadSavedPlugins(goal);
    pruneUnknownPlugins();
    // Автозаголовок, если пользователь ещё не задал своё название (важно при
    // выборе задачи с hero-экрана — там наименование мастера сразу нужно).
    if (!wizardState.value.title.trim()) {
      wizardState.value.title = defaultMockTitle(goal);
    }
    // Sensible defaults per goal
    if (goal === "procurement") {
      wizardState.value.strategy.preset = "balanced";
      wizardState.value.strategy.algos = [...DEFAULT_STRATEGY_ALGOS];
      wizardState.value.strategy.isAutoorder = 0;
    } else if (goal === "tender") {
      wizardState.value.strategy.preset = "balanced";
      wizardState.value.strategy.algos = [...DEFAULT_STRATEGY_ALGOS];
      wizardState.value.negotiation.enabled = true;
    } else if (goal === "competitors" || goal === "monitoring" || goal === "pricing") {
      // Применяем первый pivot-шаблон цели как стартовую структуру.
      // Если пользователь захочет другой — выберет чип в wizard'е, либо
      // отредактирует — тогда «активного шаблона» не будет (своя структура).
      const defaultPreset = defaultPivotPresetForGoal(goal);
      if (defaultPreset) {
        wizardState.value.analytics.pivotPreset = defaultPreset;
      }
      if (goal === "monitoring") {
        wizardState.value.analytics.schedule = "daily";
        wizardState.value.analytics.triggers = [{ id: `t-${Date.now()}`, type: "price_diff", param: 25 }];
      }
    }
  }

  function closeWizard() {
    mode.value = "list";
  }

  // ── Черновик мастера настроек (локальный, не путать с драфтами Zakooper) ────
  const initialDraft = loadSelectionDraft();
  const hasWizardDraft = ref(initialDraft !== null);
  const wizardDraftTitle = ref(initialDraft?.title ?? "");

  /** Есть ли в мастере осмысленный ввод, который стоит сохранять. */
  function wizardHasData(): boolean {
    const s = wizardState.value;
    if (!s.goal) return false;
    return s.title.trim().length > 0 || s.positions.some((p) => p.article.trim().length > 0);
  }

  function saveWizardDraft(): boolean {
    if (!wizardHasData()) return false;
    saveSelectionDraft(wizardState.value);
    hasWizardDraft.value = true;
    wizardDraftTitle.value = wizardState.value.title;
    return true;
  }

  function discardWizardDraft() {
    clearSelectionDraft();
    hasWizardDraft.value = false;
    wizardDraftTitle.value = "";
  }

  function restoreWizardDraft(): boolean {
    const draft = loadSelectionDraft();
    if (!draft) return false;
    wizardState.value = draft;
    wizardStep.value = 0;
    wizardMaxReached.value = 0;
    selectedRunUuid.value = null;
    mode.value = "wizard";
    pruneUnknownPlugins();
    void loadPlugins();
    return true;
  }

  function setStep(step: number) {
    const cap = stepCount() - 1;
    const clamped = Math.max(0, Math.min(step, cap));
    wizardStep.value = clamped;
    if (clamped > wizardMaxReached.value) wizardMaxReached.value = clamped;
  }

  function stepCount(): number {
    const goal = wizardState.value.goal;
    if (!goal) return 1; // just goal selection
    // ВАЖНО: держать в синхроне с SelectionWizard.stepKeys — иначе setStep
    // зажимает лишнее и «Продолжить» не работает (была причина, почему «Другое»
    // не листалось: без ветки → return 1 → шаг нельзя было сменить).
    if (goal === "procurement") return 4; // goal(+название) + positions + strategy + review
    if (goal === "tender") return 5; // + delivery (negotiation)
    if (goal === "other") return 4; // goal + positions + sources + review (классическая ГП)
    if (goal === "competitors") return 5; // goal + positions + sources + structure + review
    if (goal === "monitoring" || goal === "pricing") return 6; // + schedule
    return 2; // forecast: goal + review (мок)
  }

  function nextStep() {
    setStep(wizardStep.value + 1);
  }
  function prevStep() {
    setStep(wizardStep.value - 1);
  }

  function toggleAlgo(algo: number) {
    setAlgos(wizardState.value.strategy.algos.includes(algo) ? [] : [algo], [algo]);
  }

  /**
   * Включает или выключает набор алгоритмов целиком.
   *
   * Способы отбора выбираются областью поиска — запрошенный артикул, доверенные
   * аналоги, любые аналоги, — а «дешевле» и «быстрее» внутри каждой области
   * включаются вместе. Разделять их пользователю незачем: он всё равно ставит
   * обе галочки, чтобы вариантов было больше (CAB-489, просьба тех. директора).
   *
   * @param enabled какие алгоритмы должны остаться включёнными из группы
   * @param group   вся группа — из неё сначала снимаем всё
   */
  function setAlgos(enabled: readonly number[], group: readonly number[]) {
    const rest = wizardState.value.strategy.algos.filter((algo) => !group.includes(algo));
    wizardState.value.strategy.algos = [...rest, ...enabled].sort((a, b) => a - b);
    wizardState.value.strategy.preset = "custom";
  }

  /** Переключает область поиска целиком: и «дешевле», и «быстрее». */
  function toggleAlgoGroup(group: number[]) {
    const isOn = group.every((algo) => wizardState.value.strategy.algos.includes(algo));
    setAlgos(isOn ? [] : group, group);
  }

  function togglePlugin(id: number) {
    const idNum = Number(id);
    const list = wizardState.value.plugins;
    const idx = list.findIndex((p) => Number(p.id) === idNum);
    if (idx >= 0) {
      wizardState.value.plugins = list.filter((_, i) => i !== idx);
    } else {
      wizardState.value.plugins = [...list, { id: idNum, rank: 1 }];
    }
    persistSelectedPlugins();
  }

  function setPluginRank(id: number, rank: number) {
    const idNum = Number(id);
    wizardState.value.plugins = wizardState.value.plugins.map((p) => (Number(p.id) === idNum ? { ...p, rank } : p));
    persistSelectedPlugins();
  }

  function applyPluginGroup(groupId: number) {
    const allPool = [...warehouses.value, ...suppliers.value];
    const sliceStart = (groupId * 3) % Math.max(1, allPool.length - 4);
    const slice = allPool.slice(sliceStart, sliceStart + 4);
    const existingMap = new Map(wizardState.value.plugins.map((p) => [Number(p.id), p.rank]));
    for (const plugin of slice) {
      const idNum = Number(plugin.id);
      existingMap.set(idNum, existingMap.get(idNum) ?? 1);
    }
    wizardState.value.plugins = Array.from(existingMap.entries()).map(([id, rank]) => ({ id, rank }));
    persistSelectedPlugins();
  }

  function selectAllSuppliers() {
    const allIds = [...warehouses.value, ...suppliers.value].map((p) => Number(p.id));
    const prev = wizardState.value.plugins;
    wizardState.value.plugins = allIds.map((id) => {
      const found = prev.find((p) => Number(p.id) === id);
      return { id, rank: found?.rank ?? 1 };
    });
    persistSelectedPlugins();
  }

  function clearSuppliers() {
    wizardState.value.plugins = [];
    persistSelectedPlugins();
  }

  // Синхронизация выбора из таблицы поставщиков (как в одиночной проценке):
  // получаем полный список id, сохраняем ранги уже выбранных, новым — ранг 1.
  function setSupplierIds(ids: number[]) {
    const prev = new Map(wizardState.value.plugins.map((p) => [Number(p.id), p.rank]));
    wizardState.value.plugins = ids.map((id) => ({ id: Number(id), rank: prev.get(Number(id)) ?? 1 }));
    persistSelectedPlugins();
  }

  function setPivotPreset(preset: GpPivotPreset) {
    wizardState.value.analytics.pivotPreset = preset;
  }

  // ── Run creation ────────────────────────────────────────────────────────

  // Идемпотентный запуск: параллельные/повторные вызовы (двойной клик, повторный
  // emit, гонка обработчиков) возвращают ТОТ ЖЕ промис, а не создают вторую
  // закупку. Раньше кнопочный guard в wizard'е сбрасывался до завершения запуска
  // (emit не await'ится), из-за чего быстрые клики создавали 2–3 закупки разом.
  let startInFlight: Promise<SelectionRun | null> | null = null;

  function startRunFromWizard(): Promise<SelectionRun | null> {
    if (startInFlight) return startInFlight;
    isStarting.value = true;
    startInFlight = runStart().finally(() => {
      isStarting.value = false;
      startInFlight = null;
    });
    return startInFlight;
  }

  async function runStart(): Promise<SelectionRun | null> {
    const state = wizardState.value;
    const goal = state.goal;
    if (!goal) return null;
    const positions = state.positions.filter((p) => p.article.trim().length > 0);
    const plugins = await pluginsForLaunch();

    if (goal === "procurement" || goal === "tender") {
      return startPurchaseRunFromWizard(goal, positions, plugins);
    }

    if (isGsLiveGoal(goal)) {
      return startGsRunFromWizard(goal, positions);
    }

    return startMockRunFromWizard(goal, positions, plugins);
  }

  /** Тело PUT /purchase из состояния визарда. */
  function purchaseCreateBody(
    state: SelectionWizardState,
    goal: "procurement" | "tender",
    plugins: GpPluginRank[],
  ): Parameters<typeof zak.createPurchase>[0] {
    // Live API. Generate doc_osn_uuid on the fly if user provided a title but not UUID
    const docOsnUuid = state.docOsn.title ? (state.docOsn.uuid ?? `web-${generateUUID()}`) : null;
    return {
      title: nonEmpty(state.title) ?? (goal === "procurement" ? "Закупка" : "КП клиенту"),
      doc_osn_uuid: docOsnUuid,
      doc_osn_title: state.docOsn.title || null,
      is_autoorder: state.strategy.isAutoorder,
      plugins,
      // Отправляем только ВКЛЮЧённые свитчерами списки: доверенные и/или
      // исключаемые (с «!»). Выключенный список сохраняется в UI, но не уходит.
      brands_trusted: mergeBrands(
        state.strategy.brandsTrustedOn ? splitBrands(state.strategy.brandsTrusted).trusted : [],
        state.strategy.brandsExcludeOn ? splitBrands(state.strategy.brandsTrusted).excluded : [],
      ),
      purchase_deadline: state.deadline,
      extra_days: state.strategy.extraDays,
      order_type: state.strategy.orderType,
      actualization_period: 1,
      price_diff_percent: state.strategy.priceDiffPercent,
      algos: state.strategy.algos,
      comment: null,
    };
  }

  /**
   * Позиции визарда — в закупку. Пакетами: POST /items добавляет позиции, поэтому большой
   * список (до 10k) грузим частями — иначе одно тело в мегабайты рискует таймаутом/лимитом.
   */
  async function uploadPurchasePositions(
    purchaseUuid: string,
    positions: SelectionWizardState["positions"],
  ): Promise<void> {
    if (positions.length === 0) return;
    const payload = positions.map((p) => {
      const price = p.max_cost;
      return {
        article: p.article,
        brand: p.brand ?? "",
        name: p.name,
        qty: p.qty,
        ...(typeof price === "number" && Number.isFinite(price) && price > 0 ? { price } : {}),
        is_no_replace: p.is_no_replace ?? 0,
        is_important: p.is_important ?? 0,
      };
    });
    for (const batch of chunk(payload, UPLOAD_BATCH_SIZE)) {
      await zak.addPurchaseItems(purchaseUuid, batch);
    }
  }

  /** Закупка/КП: создать в zakooper, залить позиции, посеять run и запустить проценку. */
  async function startPurchaseRunFromWizard(
    goal: "procurement" | "tender",
    positions: SelectionWizardState["positions"],
    plugins: GpPluginRank[],
  ): Promise<SelectionRun | null> {
    const state = wizardState.value;
    try {
      const purchase = await zak.createPurchase(purchaseCreateBody(state, goal, plugins));
      await uploadPurchasePositions(purchase.uuid, positions);
      // Remember which goal user picked — backend doesn't distinguish.
      setGoalForUuid(purchase.uuid, goal);
      // Снимок согласования (для КП) — чтобы форма отправки предзаполнилась им
      // даже после перезагрузки страницы, пока проценка считается.
      if (goal === "tender") setNegotiationForUuid(purchase.uuid, { ...state.negotiation });
      // Seed the run immediately so UI shows "0 / <positions>" while we wait
      const seeded: SelectionRun = {
        uuid: purchase.uuid,
        purchaseUuid: purchase.uuid,
        goal,
        title: purchase.title,
        state: "pricing",
        docOsn: purchase.doc_osn_uuid ? { uuid: purchase.doc_osn_uuid, title: purchase.doc_osn_title ?? "" } : null,
        createdAt: purchase.created_at,
        updatedAt: purchase.updated_at,
        deadlineAt: purchase.purchase_deadline,
        positionsTotal: positions.length,
        positionsFinished: 0,
        totalAmount: null,
        averageTime: null,
        pluginsCount: purchase.plugins?.length ?? plugins.length,
        strategy: { ...state.strategy },
        // Сохраняем wizard-настройки согласования в run, чтобы Detail-экран
        // подтянул их в форму вместо пустого состояния. Бэк zakooper про этот
        // объект не знает — это чисто FE-«снимок» wizard'а, нужный для UX.
        negotiation: goal === "tender" ? { ...state.negotiation } : undefined,
      };
      runs.value.unshift(seeded);
      selectedRunUuid.value = seeded.uuid;
      mode.value = "detail";

      await zak.startPurchase(purchase.uuid);
      startPolling(purchase.uuid);
      return seeded;
    } catch (error) {
      listLoadError.value = error instanceof Error ? error.message : "Ошибка создания закупки";
      return null;
    }
  }

  /** Только forecast остаётся на mock — серверной поддержки нет. */
  function startMockRunFromWizard(
    goal: SelectionGoal,
    positions: SelectionWizardState["positions"],
    plugins: GpPluginRank[],
  ): SelectionRun {
    const state = wizardState.value;
    const now = new Date().toISOString();
    const run: MockRun = {
      uuid: `mock-${goal}-${Date.now()}`,
      purchaseUuid: null,
      goal,
      title: nonEmpty(state.title) ?? defaultMockTitle(goal),
      state: "pricing",
      docOsn: null,
      createdAt: now,
      updatedAt: now,
      deadlineAt: null,
      positionsTotal: positions.length || 24,
      positionsFinished: 0,
      totalAmount: null,
      averageTime: null,
      pluginsCount: plugins.length + state.analytics.competitorPluginIds.length,
      strategy: { ...state.strategy },
      analytics: { ...state.analytics },
      mockProgress: { tickStartedAt: Date.now(), durationMs: 8000 },
    };
    runs.value.unshift(run);
    selectedRunUuid.value = run.uuid;
    mode.value = "detail";
    simulateMockProgress(run.uuid);
    return run;
  }

  /**
   * Создание GS-проценки для analytics-целей (competitors/monitoring/pricing).
   *
   * Контракт (повторяем поведение Eplex 1С):
   *  1. Создаём `client_id` (UUID v4) — клиентский, нужен для PUT/POST.
   *  2. PUT /group_search → получаем `request_id`.
   *  3. PUT /group_search/{request_id}/rows с позициями.
   *  4. GET /group_search/{request_id}/start.
   *  5. Запускаем поллинг.
   *
   * При ошибке на любом шаге — пишем listLoadError и возвращаем null. Уже
   * созданный GS НЕ удаляем (если был) — пользователь увидит его в списке
   * и сможет либо удалить через UI, либо запустить руками.
   */
  async function startGsRunFromWizard(
    goal: SelectionGoal,
    positions: SelectionWizardState["positions"],
  ): Promise<SelectionRun | null> {
    const state = wizardState.value;
    const clientId = generateUUID();
    // На бэке нет деления «поставщик/конкурент» — все выбранные плагины
    // равноправны. Берём набор, сверенный с каталогом (CAB-696).
    const ownPlugins = await pluginsForLaunch();
    const pluginIds = Array.from(new Set(ownPlugins.map((p) => Number(p.id)))).filter((id) => id > 0);

    const settings = buildGsSettings({
      goal,
      title: nonEmpty(state.title) ?? defaultMockTitle(goal),
      pluginIds,
      strategy: state.strategy,
      analytics: state.analytics,
    });

    let requestId: string;
    try {
      const created = await gs.createGroupSearch({ client_id: clientId, settings });
      requestId = created.request_id;
      if (goal === "other") {
        // Срок уже применён при запуске: фиксируем его до /start и загрузки выдачи,
        // чтобы F5 или неполные настройки в следующем ответе его не отменили.
        initializeSelectionDeliveryTerm(requestId, readGsDeliveryTerm(settings));
      }

      if (positions.length > 0) {
        const rows: gs.GsRowInputItem[] = positions.map((p) => ({
          article: p.article,
          brand: p.brand ?? "",
          settings: {
            max_cost: typeof p.max_cost === "number" ? p.max_cost : undefined,
            required_count: p.qty,
          },
          add_info: { e0ProductID: p.article },
        }));
        await gs.putGroupSearchRowsBatched(requestId, rows, UPLOAD_BATCH_SIZE);
      }
    } catch (error) {
      listLoadError.value = error instanceof Error ? error.message : "Не удалось создать групповую проценку";
      return null;
    }

    // Нехватка дневного/часового лимита проценок: не запускаем вовсе — иначе бэк
    // оборвёт ГП на середине и она сразу уедет в «Завершённые» с дырявым результатом.
    // Сохраняем черновиком с возможностью запуска позже (кнопка на карточке).
    const limitShortage = await gsStartShortage(settings.mode, positions.length);

    // Создание/добавление прошло — сохраняем seed-run ДО /start, чтобы UI
    // отображал запись даже если /start вернёт «already running» (так бывает
    // у pricekeeper: первый цикл запускается автоматически).
    const uuid = `gs-${requestId}`;
    setGoalForUuid(uuid, goal);
    const now = new Date().toISOString();
    const seeded: SelectionRun = {
      uuid,
      purchaseUuid: null,
      gsRequestId: requestId,
      gsClientId: clientId,
      goal,
      title: settings.name,
      state: limitShortage > 0 ? "draft" : "pricing",
      docOsn: null,
      createdAt: now,
      updatedAt: now,
      deadlineAt: null,
      positionsTotal: positions.length,
      positionsFinished: 0,
      totalAmount: null,
      averageTime: null,
      pluginsCount: pluginIds.length,
      strategy: { ...state.strategy },
      analytics: { ...state.analytics },
    };
    runs.value.unshift(seeded);
    selectedRunUuid.value = uuid;
    mode.value = "detail";

    if (limitShortage > 0) {
      useNotifications().notify(i18n.global.t("selection.limits.savedAsDraft", { n: limitShortage }), "info");
      return seeded;
    }

    const startError = await startGroupSearchTolerant(requestId);
    if (startError) listLoadError.value = startError;

    startGsPolling(requestId);
    return seeded;
  }

  /**
   * `/start` группового поиска с поправкой на штатные отказы бэка. Он отвечает 407, когда:
   *  - pricekeeper уже автозапустился сразу после PUT rows;
   *  - tender/pricekeeper требуют ≥5 позиций (для tender первый цикл тоже
   *    запускается автоматически после добавления нужного числа).
   * Такие ответы — норма для analytics flow, seed остаётся без ошибки (null).
   * Любая другая ошибка возвращается текстом — решает вызывающий.
   */
  async function startGroupSearchTolerant(requestId: string): Promise<string | null> {
    try {
      await gs.startGroupSearch(requestId);
      return null;
    } catch (error) {
      const innerErr = getBackendInnerError(error);
      const isExpected407 = innerErr
        ? /уже запущен|already running|минимальное количество позиций|error_code"?\s*:\s*407/i.test(innerErr)
        : false;
      if (isExpected407) return null;
      return innerErr ?? (error instanceof Error ? error.message : String(error));
    }
  }

  /**
   * Сколько строк не влезает в лимит проценок (день/час); 0 — можно запускать.
   * Ошибку получения лимитов не считаем блокером — просто разрешаем запуск.
   */
  async function gsStartShortage(mode: gs.GsMode, rowsCount: number): Promise<number> {
    try {
      const limits = await gs.getAuthLimits();
      const scope = mode === "pricekeeper" ? limits.gs_pricekeeper : limits.gs_newsearch;
      if (!scope) return 0;
      const availableDay = Math.max(0, scope.day.max - scope.day.cur);
      const availableHour = Math.max(0, scope.hour.max - scope.hour.cur);
      const available = Math.min(availableDay, availableHour);
      return rowsCount > available ? rowsCount - available : 0;
    } catch (error) {
      console.error("Failed to check group search limits: ", error);
      return 0;
    }
  }

  /**
   * Повторить подбор, чьи результаты бэк удалил по сроку хранения (CAB-677).
   *
   * Перезапустить прежнюю сущность нельзя: `/purchases/{uuid}/start` после удаления
   * группового поиска отвечает тем же «Указанный id не найден», а сам ГП удалён
   * целиком. Поэтому создаём новую копию «один в один» (настройки + позиции), сразу
   * запускаем и делаем её текущей — пользователь оказывается в новом подборе, а
   * прежний остаётся в списке как есть.
   *
   * `null` — копировать нечего: групповой поиск ручного режима удалён вместе с
   * позициями. Остальные ошибки пробрасываются — вызывающий покажет их человеку.
   */
  async function repeatRun(run: SelectionRun): Promise<SelectionRun | null> {
    if (isRepeating.value) return null;
    isRepeating.value = true;
    try {
      if (run.purchaseUuid) return await repeatPurchaseRun(run, run.purchaseUuid);
      if (run.gsRequestId) return await repeatGsRun(run, run.gsRequestId);
      return null;
    } finally {
      isRepeating.value = false;
    }
  }

  /** Закупка/КП: копия закупки zakooper с теми же настройками и позициями, сразу в проценку. */
  async function repeatPurchaseRun(run: SelectionRun, purchaseUuid: string): Promise<SelectionRun> {
    const [source, items] = await Promise.all([zak.getPurchase(purchaseUuid), zak.listPurchaseItems(purchaseUuid)]);
    const purchase = await zak.createPurchase(purchaseClonePayload(source));
    for (const batch of chunk(items.data.map(purchaseItemClonePayload), UPLOAD_BATCH_SIZE)) {
      await zak.addPurchaseItems(purchase.uuid, batch);
    }
    setGoalForUuid(purchase.uuid, run.goal);
    if (run.negotiation) setNegotiationForUuid(purchase.uuid, { ...run.negotiation });

    // Seed до /start — карточка новой закупки открывается сразу, с «0 из N».
    const seeded: SelectionRun = {
      ...mapZakPurchase(purchase, run.goal, items.data.length, 0, null, run.negotiation),
      state: "pricing",
      positionsTotal: items.data.length,
      positionsFinished: 0,
      pluginsCount: purchase.plugins.length,
    };
    runs.value.unshift(seeded);
    selectedRunUuid.value = seeded.uuid;
    mode.value = "detail";

    await zak.startPurchase(purchase.uuid);
    startPolling(purchase.uuid);
    return seeded;
  }

  /**
   * Ручной режим и аналитика: копия группового поиска с теми же настройками и
   * строками. Если прежний ГП уже удалён по сроку хранения — копировать нечего.
   */
  async function repeatGsRun(run: SelectionRun, gsRequestId: string): Promise<SelectionRun | null> {
    let source: gs.GsItemDto;
    let rows: gs.GsRowInputItem[];
    try {
      const [gsItem, gsRows] = await Promise.all([gs.getGroupSearch(gsRequestId), gs.listGroupSearchRows(gsRequestId)]);
      source = gsItem.item;
      rows = gsRows.items.map(gsRowClonePayload);
    } catch (error) {
      if (isExpiredSearchResultsError(error)) return null;
      throw error;
    }

    const clientId = generateUUID();
    const settings = groupSearchClonePayload(source);
    const created = await gs.createGroupSearch({ client_id: clientId, settings });
    if (rows.length > 0) await gs.putGroupSearchRowsBatched(created.request_id, rows, UPLOAD_BATCH_SIZE);

    const limitShortage = await gsStartShortage(settings.mode, rows.length);
    const uuid = `gs-${created.request_id}`;
    setGoalForUuid(uuid, run.goal);
    const now = new Date().toISOString();
    const seeded: SelectionRun = {
      uuid,
      purchaseUuid: null,
      gsRequestId: created.request_id,
      gsClientId: clientId,
      goal: run.goal,
      title: settings.name,
      state: limitShortage > 0 ? "draft" : "pricing",
      docOsn: null,
      createdAt: now,
      updatedAt: now,
      deadlineAt: null,
      positionsTotal: rows.length,
      positionsFinished: 0,
      totalAmount: null,
      averageTime: null,
      pluginsCount: settings.plugins.length,
      strategy: { ...run.strategy, algos: [...run.strategy.algos] },
      analytics: run.analytics ? { ...run.analytics } : undefined,
    };
    runs.value.unshift(seeded);
    selectedRunUuid.value = uuid;
    mode.value = "detail";

    if (limitShortage > 0) {
      useNotifications().notify(i18n.global.t("selection.limits.savedAsDraft", { n: limitShortage }), "info");
      return seeded;
    }
    const startError = await startGroupSearchTolerant(created.request_id);
    if (startError) throw new Error(startError);
    startGsPolling(created.request_id);
    return seeded;
  }

  /**
   * Запуск сохранённой (черновой) групповой проценки с карточки — например,
   * когда дневной лимит освободился.
   */
  async function startGsRun(uuid: string): Promise<boolean> {
    const run = runs.value.find((r) => r.uuid === uuid);
    if (!run?.gsRequestId) return false;
    const gsMode: gs.GsMode = run.goal === "monitoring" || run.goal === "pricing" ? "pricekeeper" : "manual";
    const shortage = await gsStartShortage(gsMode, run.positionsTotal);
    if (shortage > 0) {
      useNotifications().notify(i18n.global.t("selection.limits.stillShort", { n: shortage }), "info");
      return false;
    }
    try {
      await gs.startGroupSearch(run.gsRequestId);
    } catch (error) {
      const innerErr = getBackendInnerError(error);
      useNotifications().notify(innerErr ?? i18n.global.t("selection.limits.startFailed"), "error");
      return false;
    }
    const idx = runs.value.findIndex((r) => r.uuid === uuid);
    if (idx >= 0) runs.value[idx] = { ...runs.value[idx], state: "pricing" };
    startGsPolling(run.gsRequestId);
    return true;
  }

  function defaultMockTitle(goal: SelectionGoal): string {
    const map: Record<SelectionGoal, string> = {
      procurement: "Закупка",
      tender: "КП клиенту",
      other: "Проценка",
      competitors: "Сверка с конкурентами",
      monitoring: "Мониторинг цен",
      pricing: "Ценообразование",
      forecast: "Прогноз потребности",
    };
    return `${map[goal]} · ${new Date().toLocaleDateString("ru-RU")}`;
  }

  function simulateMockProgress(uuid: string) {
    const run = runs.value.find((r) => r.uuid === uuid) as MockRun | undefined;
    if (!run?.mockProgress) return;
    const timer = setInterval(() => {
      const elapsed = Date.now() - run.mockProgress.tickStartedAt;
      const ratio = Math.min(1, elapsed / run.mockProgress.durationMs);
      run.positionsFinished = Math.round(run.positionsTotal * ratio);
      run.updatedAt = new Date().toISOString();
      if (ratio >= 1) {
        run.state = "completed";
        clearInterval(timer);
      }
    }, 1000);
  }

  // ── Polling: единый батч-тик вместо N поллеров-по-id ─────────────────────
  //
  // Раньше на каждую активную закупку/GS заводился свой setInterval с per-id
  // запросом (getPurchase + вложенный getGroupSearch, либо getGroupSearch
  // per-id). Десятки активных процессов = десятки таймеров × 2 запроса каждые
  // 4 с → упор в лимит запросов и 20-минутные локи у пользователя. Теперь один
  // общий таймер за тик делает максимум: 1× listPurchases (все наблюдаемые
  // закупки разом) + 1× getGroupSearchFlows (дельта по всем GS) + не более
  // одного точечного getGroupSearch для реалтайм-прогресса ОТКРЫТОЙ карточки.

  // dfrom для group_search/flows — валидный timestamp «YYYY-MM-DD HH:MM:SS».
  // Эпоху (1970) бэк отклоняет (error_code 407 «Неверный параметр запроса: dfrom»),
  // поэтому стартуем с текущего времени; актуальное состояние подтягиваем через
  // getGroupSearch/rows, а flows отдаёт только дельты.
  const observedPurchaseUuids = new Set<string>();
  const observedGsRequestIds = new Set<string>();
  // Проценки, которым отправлен стоп, но бэк ещё не подтвердил его статусом
  // (CAB-738). Реактивный Set: карточка показывает «Останавливаем…» сразу.
  const stoppingGsRequestIds = ref<Set<string>>(new Set());
  const stopConfirmDeadlines = new Map<string, number>();
  /** Сколько ждём подтверждения остановки, прежде чем вернуть честное «идёт проценка». */
  const STOP_CONFIRM_TIMEOUT_MS = 90_000;
  let batchTimer: ReturnType<typeof setInterval> | null = null;
  let gsFlowsDfrom = formatDate(new Date());

  function ensureBatchTimer() {
    if (batchTimer) return;
    batchTimer = setInterval(() => {
      runBatchTick().catch((error) => console.error("Selection batch poll failed:", error));
    }, POLL_INTERVAL_MS);
  }

  function stopBatchTimer() {
    if (batchTimer) clearInterval(batchTimer);
    batchTimer = null;
  }

  function isRunFinished(state: SelectionState): boolean {
    return state === "completed" || state === "cancelled" || state === "archived";
  }

  /** Снимаем с наблюдения процессы, которых уже нет или которые завершились. */
  function pruneObserved() {
    for (const uuid of [...observedPurchaseUuids]) {
      const run = runs.value.find((r) => r.uuid === uuid);
      if (!run || !isLiveGoal(run.goal) || isRunFinished(run.state)) {
        observedPurchaseUuids.delete(uuid);
      }
    }
    for (const reqId of [...observedGsRequestIds]) {
      const run = runs.value.find((r) => r.gsRequestId === reqId);
      if (!run || isRunFinished(run.state)) {
        observedGsRequestIds.delete(reqId);
      }
    }
  }

  async function runBatchTick() {
    pruneObserved();
    // Проценка с отправленным стопом держит тик даже без наблюдения: пока бэк не
    // подтвердит остановку, карточке есть что показывать и что дожимать.
    const hasStopping = stoppingGsRequestIds.value.size > 0;
    if (observedPurchaseUuids.size === 0 && observedGsRequestIds.size === 0 && !hasStopping) {
      stopBatchTimer();
      return;
    }
    const jobs: Promise<unknown>[] = [];
    if (observedPurchaseUuids.size > 0) jobs.push(tickPurchases());
    if (observedGsRequestIds.size > 0 || hasStopping) jobs.push(tickGroupSearches());
    await Promise.allSettled(jobs);
  }

  // Разовый тик сразу при открытии карточки — чтобы после перезагрузки страницы
  // прогресс подтягивался мгновенно, а не через POLL_INTERVAL_MS.
  function kickImmediateTick() {
    runBatchTick().catch((error) => console.error("Selection batch poll failed:", error));
  }

  async function fetchOpenDetailGsInfo(purchase: {
    gs_request_id?: string | null;
    state: number;
  }): Promise<{ total: number; finished: number } | null> {
    if (!purchase.gs_request_id || purchase.state > 2) return null;
    try {
      const { item } = await gs.getGroupSearch(purchase.gs_request_id);
      if (!item.info) return null;
      return { total: item.info.total ?? 0, finished: item.info.finished ?? 0 };
    } catch {
      return null;
    }
  }

  async function tickPurchases() {
    const list = await zak.listPurchases({
      limit: 50,
      order_by: "updated_at",
      order_dir: "DESC",
      is_archive: 0,
    });
    const openUuid = selectedRunUuid.value;
    for (const uuid of [...observedPurchaseUuids]) {
      const purchase = list.data.find((x) => x.uuid === uuid);
      if (!purchase) continue;
      // Реалтайм-прогресс из group_search тянем только для открытой карточки —
      // максимум один точечный запрос за тик, а не по одному на каждую закупку.
      const gsLiveInfo = uuid === openUuid ? await fetchOpenDetailGsInfo(purchase) : null;
      const idx = runs.value.findIndex((r) => r.uuid === uuid);
      const goal = goalByUuid.value[uuid] ?? runs.value[idx]?.goal ?? "procurement";
      const prevTotal = idx >= 0 ? runs.value[idx].positionsTotal : 0;
      const prevFinished = idx >= 0 ? runs.value[idx].positionsFinished : 0;
      const prevNegotiation = (idx >= 0 ? runs.value[idx].negotiation : undefined) ?? negotiationByUuid.value[uuid];
      const mapped = mapZakPurchase(purchase, goal, prevTotal, prevFinished, gsLiveInfo, prevNegotiation);
      if (idx >= 0) runs.value[idx] = mapped;
      else runs.value.unshift(mapped);
    }
  }

  async function tickGroupSearches() {
    await confirmGsStops();
    const flows = await gs.getGroupSearchFlows(gsFlowsDfrom);
    if (flows.time) gsFlowsDfrom = flows.time;
    if (flows.items.length === 0) return;
    const byRequestId = new Map(flows.items.map((it) => [it.request_id, it]));
    for (const reqId of [...observedGsRequestIds]) {
      const item = byRequestId.get(reqId);
      if (!item) continue; // за эту дельту по этой проценке изменений не было
      const uuid = `gs-${item.request_id}`;
      const idx = runs.value.findIndex((r) => r.uuid === uuid);
      const goal = goalByUuid.value[uuid] ?? runs.value[idx]?.goal ?? modeToAnalyticsGoal(item.settings.mode);
      const prevTotal = idx >= 0 ? runs.value[idx].positionsTotal : 0;
      const mapped = mapGsItemToRun(item, goal, prevTotal);
      if (idx >= 0) runs.value[idx] = mapped;
      else runs.value.unshift(mapped);
      settleGsStopFlag(item.request_id, mapped.state);
    }
  }

  /**
   * Останавливаемые проценки спрашиваем напрямую, а не ждём их в дельте `flows`:
   * остановка — как раз тот момент, когда проценка перестаёт «шевелиться», и
   * дельта может прийти с задержкой. Список короткий и живёт секунды, так что
   * экономию запросов батч-тика это не ломает.
   */
  async function confirmGsStops() {
    for (const requestId of [...stoppingGsRequestIds.value]) {
      settleGsStopFlag(requestId);
      if (!stoppingGsRequestIds.value.has(requestId)) continue;
      await reloadGsOne(requestId);
    }
  }

  function startPolling(uuid: string) {
    observedPurchaseUuids.add(uuid);
    ensureBatchTimer();
  }

  function stopPolling(uuid: string) {
    observedPurchaseUuids.delete(uuid);
  }

  function stopAllPolling() {
    observedPurchaseUuids.clear();
    observedGsRequestIds.clear();
    stopBatchTimer();
  }

  // сброс состояния при смене аккаунта (логаут/логин): иначе каунтер запущенных процессов
  // и список остаются от прошлого пользователя до перезагрузки страницы
  function reset() {
    stopAllPolling();
    stoppingGsRequestIds.value.clear();
    stopConfirmDeadlines.clear();
    runs.value = [];
    selectedRunUuid.value = null;
    mode.value = "list";
    isLoadingList.value = false;
    listLoaded.value = false;
    listLoadError.value = null;
    goalByUuid.value = getSavedSelectionGoals();
    negotiationByUuid.value = getSavedSelectionNegotiations();
    purchaseOrdersSettledByUuid.value = {};
  }

  function selectRun(uuid: string | null) {
    selectedRunUuid.value = uuid;
    mode.value = uuid ? "detail" : "list";
    if (!uuid) return;
    const run = runs.value.find((r) => r.uuid === uuid);
    if (!run) return;
    if (isLiveGoal(run.goal)) {
      startPolling(uuid);
      kickImmediateTick();
    } else if (isGsLiveGoal(run.goal) && run.gsRequestId) {
      startGsPolling(run.gsRequestId);
    }
  }

  /**
   * Остановить проценку на движке group_search (`GET /group_search/{id}/stop`) —
   * «Ручной режим» и аналитические цели. Ошибку не глушим: карточка сама решает,
   * что показать, и не перетирает `listLoadError`, который отвечает за загрузку
   * списка, а не за одиночное действие.
   *
   * Очередь на бэке гаснет не мгновенно: замер на стенде — `/stop` принят сразу,
   * а статус остаётся «идёт» ещё 10–20 с, пока площадки не свернут запросы. Всё
   * это время карточка показывала «Идёт проценка», и было не понять, сработало
   * ли (CAB-738). Поэтому помечаем проценку как останавливаемую — UI показывает
   * «Останавливаем…» — и дожимаем финальный статус прямым опросом.
   */
  async function stopGsRun(requestId: string): Promise<void> {
    await gs.stopGroupSearch(requestId);
    markGsStopping(requestId);
    const updated = await reloadGsOne(requestId);
    if (updated && isRunFinished(updated.state)) return;
    startGsPolling(requestId);
  }

  // ── Останавливаемые проценки: окно между «бэк принял /stop» и финальным статусом ──

  function markGsStopping(requestId: string) {
    stoppingGsRequestIds.value.add(requestId);
    stopConfirmDeadlines.set(requestId, Date.now() + STOP_CONFIRM_TIMEOUT_MS);
  }

  function clearGsStopping(requestId: string) {
    stoppingGsRequestIds.value.delete(requestId);
    stopConfirmDeadlines.delete(requestId);
  }

  /**
   * Проценке отправлен стоп, но бэк ещё не подтвердил его статусом. Карточка
   * показывает «Останавливаем…» и не даёт нажать «Остановить» повторно.
   */
  function isGsStopping(requestId: string | null | undefined): boolean {
    return requestId ? stoppingGsRequestIds.value.has(requestId) : false;
  }

  /**
   * Снять пометку, когда ждать больше нечего: проценка стала финальной (стоп
   * подтверждён) или вышло время ожидания — тогда не врём «Останавливаем…»
   * бесконечно, а возвращаем фактическое состояние и право нажать ещё раз.
   */
  function settleGsStopFlag(requestId: string, state?: SelectionState) {
    if (!stoppingGsRequestIds.value.has(requestId)) return;
    if (state && isRunFinished(state)) {
      clearGsStopping(requestId);
      return;
    }
    const deadline = stopConfirmDeadlines.get(requestId) ?? 0;
    if (Date.now() >= deadline) clearGsStopping(requestId);
  }

  async function deleteGsRun(requestId: string): Promise<boolean> {
    try {
      await gs.deleteGroupSearch(requestId);
      const uuid = `gs-${requestId}`;
      runs.value = runs.value.filter((r) => r.uuid !== uuid);
      if (selectedRunUuid.value === uuid) {
        selectedRunUuid.value = null;
        mode.value = "list";
      }
      return true;
    } catch (error) {
      listLoadError.value = error instanceof Error ? error.message : "Не удалось удалить";
      return false;
    }
  }

  /**
   * Подписать GS-проценку на общий батч-тик (см. {@link ensureBatchTimer}).
   * Наблюдение снимается автоматически, когда проценка завершена/отменена
   * или исчезла из списка ({@link pruneObserved}).
   */
  function startGsPolling(requestId: string) {
    observedGsRequestIds.add(requestId);
    // dfrom = «сейчас»: flows отдаёт дельты после старта наблюдения, а текущее
    // состояние проценки берём из getGroupSearch/rows (эпоху бэк отклоняет).
    gsFlowsDfrom = formatDate(new Date());
    ensureBatchTimer();
  }

  return {
    runs,
    algoDescriptions,
    isLoadingList,
    listLoaded,
    listLoadError,
    isStarting,
    isRepeating,
    warehouses,
    suppliers,
    pluginGroups,
    trustedBrandsCatalog,
    isLoadingPlugins,
    pluginsLoadError,
    loadPlugins,
    resetPluginCatalog,
    analyticsDataset: analyticsDatasetRef,
    fieldDefinitions,

    mode,
    wizardState,
    wizardStep,
    wizardMaxReached,
    searchQuery,
    goalFilter,

    filteredRuns,
    groupedRuns,
    selectedRunUuid,
    selectedRun,
    activeProcesses,
    completedProcesses,

    isLiveGoal,
    stateFromZak,
    stepCount,

    loadList,
    reloadOne,
    startGsRun,
    repeatRun,

    openWizard,
    openWizardFromRun,
    closeWizard,
    hasWizardDraft,
    wizardDraftTitle,
    wizardHasData,
    saveWizardDraft,
    discardWizardDraft,
    restoreWizardDraft,
    pickGoal,
    setStep,
    nextStep,
    prevStep,
    toggleAlgo,
    toggleAlgoGroup,
    togglePlugin,
    setPluginRank,
    applyPluginGroup,
    selectAllSuppliers,
    clearSuppliers,
    setSupplierIds,
    setPivotPreset,

    startRunFromWizard,
    selectRun,
    markPurchaseOrdersSettled,
    startPolling,
    stopPolling,
    stopAllPolling,
    startGsPolling,
    reloadGsOne,
    stopGsRun,
    isGsStopping,
    deleteGsRun,
    reset,
  };
});
